import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import LiveThread from '../components/LiveThread';

// Duplicated from Gestion.js — shared source. The helpers below resolve the
// effective allowed file types / max size for a gestion thread, falling back
// to the org-level config from AppConfig. Kept in sync with Gestion.js:76-98.
function effectiveGestionThreadAllowedTypes(typeConfig) {
  if (typeConfig && Array.isArray(typeConfig.tipos_archivo_permitidos_thread) && typeConfig.tipos_archivo_permitidos_thread.length > 0) {
    return typeConfig.tipos_archivo_permitidos_thread;
  }
  // Fall back to tipos_archivo_permitidos
  if (typeConfig && Array.isArray(typeConfig.tipos_archivo_permitidos) && typeConfig.tipos_archivo_permitidos.length > 0) {
    return typeConfig.tipos_archivo_permitidos;
  }
  const raw = AppConfig.get('gestiones_allowed_file_types');
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return ['image/jpeg', 'image/png', 'image/webp'];
}

function effectiveGestionMaxMb(typeConfig) {
  if (typeConfig && typeConfig.tamano_max_archivo_mb) {
    return typeConfig.tamano_max_archivo_mb;
  }
  const raw = AppConfig.get('gestiones_max_file_mb');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export default function GestionChat({ setNavbarState }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [gestion, setGestion] = React.useState({});
  const [typeConfig, setTypeConfig] = React.useState(null);
  const [deleteCommentDialog, setDeleteCommentDialog] = React.useState({ open: false, commentId: null });
  const [deletePending, setDeletePending] = React.useState(false);
  const [openImage, setOpenImage] = React.useState(null);
  // Monotonic sequence for fetchGestion — a stale response must never
  // overwrite a newer one (silent refetches can overlap).
  const fetchSeqRef = React.useRef(0);
  // Scroll container for the message list (chat continuity).
  const scrollContainerRef = React.useRef(null);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchTypeConfig = React.useCallback(async (typeKey) => {
    if (!typeKey) return null;
    try {
      const resp = await apiClient.get('gestiones/types', {}, { action: 'fetch_gestion_types' });
      if (resp.status === 200) {
        const types = (resp.data && resp.data.data && resp.data.data.types) || [];
        const cfg = types.find((t) => t.key === typeKey) || null;
        setTypeConfig(cfg);
        return cfg;
      }
    } catch (error) {
      console.error('Error fetching gestion types:', error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      }
    }
    return null;
  }, []);

  // { initial: true } shows the loading backdrop (first load only). Silent
  // refreshes (default) update the gestion in place so the thread subtree
  // stays mounted — no whole-UI redraw on new messages.
  const fetchGestion = React.useCallback(({ initial } = {}) => {
    const seq = ++fetchSeqRef.current;
    if (initial) {
      setShowBackdrop(true);
      setLoading(true);
    }
    return apiClient.get(`gestion/get/${id}`, {}, { action: 'fetch_gestion' })
      .then(async (response) => {
        if (seq !== fetchSeqRef.current) return;
        if (response.status === 200) {
          const data = response.data.data;
          const estados = data.estados || [];
          data.estado = estados[estados.length - 1] || { estado: '', fecha: 0 };
          setGestion(data);
          await fetchTypeConfig(data.type_key);
        }
      })
      .catch((error) => {
        if (seq !== fetchSeqRef.current) return;
        console.error('Error fetching gestion:', error);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' });
        } else if (initial) {
          // Only bail out to the hub when the initial load fails — a failed
          // silent refetch keeps the existing gestion on screen.
          setSnackbar({ open: true, message: 'Hubo un error obteniendo los detalles de la gestión', severity: 'error' });
          setRedirect({ trigger: true, route: '/adminhub' });
        }
      })
      .finally(() => {
        if (initial && seq === fetchSeqRef.current) {
          setShowBackdrop(false);
          setLoading(false);
        }
      });
  }, [id, fetchTypeConfig]);

  React.useEffect(() => {
    if (setNavbarState) {
      setNavbarState((prev) => ({ ...(prev || {}), showBack: false, showOptions: false, showSearch: false }));
    }
  }, [setNavbarState]);

  React.useEffect(() => {
    fetchGestion({ initial: true });
  }, [id, fetchGestion]);

  // Mark-as-read on mount (§5.5): set the localStorage key to now (epoch seconds).
  React.useEffect(() => {
    if (!id) return undefined;
    try {
      localStorage.setItem(`mgo:gestion-read:${id}`, String(Math.floor(Date.now() / 1000)));
    } catch (e) { /* ignore */ }
    return undefined;
  }, [id]);

  // Duplicated from Gestion.js — shared source. The handlers below own the
  // API calls for live comments; LiveThread owns the input state. Kept in
  // sync with Gestion.js:411-478.
  // Returns a promise resolving to true/false so LiveThread knows whether to
  // clear the composer (kept on failure). The POST response carries no
  // comment payload, so on success we silently refetch the canonical gestion.
  const handleLiveSubmitComment = ({ text, files }) => {
    if (text.trim() === '' && (!files || files.length === 0)) {
      setSnackbar({ open: true, message: 'No puedes enviar un comentario vacío.', severity: 'error' });
      return Promise.resolve(false);
    }
    const formData = new FormData();
    formData.append('comentario', text);
    (files || []).forEach((f) => formData.append('adjuntos', f));
    return apiClient.post(`gestion/comment/${id}`, formData, {}, { action: 'add_gestion_comment' })
      .then((response) => {
        if (response.status === 200) {
          fetchGestion();
          return true;
        }
        return false;
      })
      .catch((error) => {
        const msg = (error && error.data && error.data.data && error.data.data.mensaje)
          || getErrorMessage(error, 'Hubo un error al agregar tu comentario.');
        setSnackbar({ open: true, message: msg, severity: 'error' });
        return false;
      });
  };

  const handleLiveCommentsChange = (updater) => {
    setGestion((prev) => {
      const prevList = (prev && prev.comentarios) ? prev.comentarios : [];
      const next = typeof updater === 'function' ? updater(prevList) : updater;
      // Mark-as-read (§5.5): on each new incoming comment, set the key to the
      // latest comment fecha (epoch seconds) or now.
      if (Array.isArray(next) && next.length > 0) {
        const latest = next[next.length - 1];
        const ts = (latest && Number(latest.fecha)) || Math.floor(Date.now() / 1000);
        try {
          localStorage.setItem(`mgo:gestion-read:${id}`, String(ts));
        } catch (e) { /* ignore */ }
      }
      return { ...prev, comentarios: next };
    });
  };

  const handleLiveEntityUpdate = () => {
    // Silently refresh the gestion to pick up state / field changes
    // (also covers the WS reconnect { refetch: true } signal).
    fetchGestion();
  };

  const handleLiveDeleteComment = (comentario) => {
    setDeleteCommentDialog({ open: true, commentId: comentario.fecha });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteCommentDialog({ open: false, commentId: null });
  };

  const handleDeleteComment = () => {
    setDeletePending(true);
    apiClient.post(`gestion/delete/comment/${id}`, { comentario_id: deleteCommentDialog.commentId }, {}, { action: 'delete_gestion_comment' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Comentario eliminado correctamente', severity: 'success' });
          fetchGestion();
        }
        setDeletePending(false);
        handleCloseDeleteDialog();
      })
      .catch((error) => {
        console.error('Error deleting comment:', error);
        setSnackbar({ open: true, message: 'Hubo un error al intentar eliminar el comentario.', severity: 'error' });
        setDeletePending(false);
        handleCloseDeleteDialog();
      });
  };

  const handleOpenImage = (src) => setOpenImage(src);
  const handleCloseImage = () => setOpenImage(null);

  const estados = (typeConfig && typeConfig.estados) || {};
  const currentState = gestion.estado && gestion.estado.estado;
  const currentStateCfg = estados[currentState] || null;
  // Product decision: comments are allowed on any state for any role.
  // The composer is always rendered; the backend enforces permissions.
  const canComment = true;

  return (
    <Container
      maxWidth="sm"
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Bounded height (viewport minus the fixed bottom nav) so the message
        // list below becomes the scroll container and the composer can pin.
        height: 'calc(100vh - var(--app-bottom-nav-height, 0px))',
        '@supports (height: 100dvh)': {
          height: 'calc(100dvh - var(--app-bottom-nav-height, 0px))',
        },
      }}
    >
      {redirect.trigger && <Navigate to={redirect.route} replace={true} />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: '3.2rem' }}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {!loading && gestion && gestion.id && (
        // minHeight:0 is required so this flex item can shrink below its
        // content height — otherwise its auto min-height keeps the whole
        // page scrolling instead of the message list.
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Sticky header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <IconButton onClick={() => navigate(`/gestion/${id}`)} sx={{ color: 'text.primary', ml: -1 }} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {gestion.titulo}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={currentStateCfg ? currentStateCfg.nombre_corto : (estados[currentState] ? estados[currentState].nombre_corto : currentState)}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: '0.7rem', backgroundColor: 'rgba(233,203,167,0.5)', color: '#F2700C', borderRadius: 1, height: 20 }}
                />
                <Typography variant="caption" color="text.secondary">en línea</Typography>
              </Box>
            </Box>
          </Box>

          {/* Message list — scrolls internally; the composer's mt:'auto'
              inside LiveThread anchors it to the bottom on short threads. */}
          <Box ref={scrollContainerRef} sx={{ flex: 1, minHeight: 0, px: 2, py: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <LiveThread
              gestionId={id}
              type="gestion"
              variant="chat"
              comments={gestion.comentarios || []}
              onCommentsChange={handleLiveCommentsChange}
              onEntityUpdate={handleLiveEntityUpdate}
              estados={estados}
              canComment={canComment}
              onSubmitComment={handleLiveSubmitComment}
              onDeleteComment={handleLiveDeleteComment}
              currentUser={AuthService.getCurrentUserEmail()}
              allowedFileTypes={effectiveGestionThreadAllowedTypes(typeConfig)}
              maxFileMb={effectiveGestionMaxMb(typeConfig)}
              onOpenImage={handleOpenImage}
              scrollContainerRef={scrollContainerRef}
            />
          </Box>
        </Box>
      )}

      <Dialog
        open={deleteCommentDialog.open}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="del-dialog-title"
        aria-describedby="del-dialog-description"
      >
        <DialogTitle id="del-dialog-title">
          ¿Estás seguro de que quieres eliminar este comentario?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="del-dialog-description">
            Esta acción no se puede deshacer. El comentario será eliminado permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancelar</Button>
          <Button onClick={handleDeleteComment} color="secondary" variant="contained" autoFocus disabled={deletePending}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!openImage} onClose={handleCloseImage} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, textAlign: 'center', backgroundColor: 'rgba(0,0,0,0.85)' }}>
          {openImage && (
            <img src={openImage} alt="Imagen" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImage} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
